import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 4);

  const formatViews = (views: number) => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  const comicSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicSeries',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `http://nikson.cn/comic/${comic.id}`,
    genre: comic.tags,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫画网',
      url: 'http://nikson.cn/',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: comic.views,
    },
  };

  return (
    <>
      <SEO
        title={comic.title}
        description={`${comic.title} - ${comic.description.substring(0, 120)}`}
        keywords={`${comic.title},${comic.tags.join(',')},韩漫画网,韩国漫画`}
        canonicalUrl={`http://nikson.cn/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        schema={comicSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: comic.tags[0] ? comic.tags[0] : '漫画', to: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="px-4 md:px-6 py-8 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-56 rounded-xl overflow-hidden shadow-sm">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫画网`}
                    className="w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">
                  作者：<strong className="text-foreground-700">{comic.author}</strong>
                </p>

                <div className="flex items-center gap-4 mb-4 flex-wrap">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">/ 10</span>
                  </div>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-eye-line mr-1"></i>
                    {formatViews(comic.views)} 阅读
                  </span>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className={`text-sm font-medium ${comic.status === '已完结' ? 'text-accent-600' : 'text-primary-500'}`}>
                    {comic.status === '已完结' ? (
                      <i className="ri-check-double-line mr-1"></i>
                    ) : (
                      <i className="ri-loader-4-line mr-1"></i>
                    )}
                    {comic.status}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-5 flex-wrap">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex gap-3">
                  <button className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
                    <i className="ri-play-circle-line"></i>
                    开始阅读第一话
                  </button>
                  <button className="flex items-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-200 transition-colors whitespace-nowrap">
                    <i className="ri-bookmark-line"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-12">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                章节列表
                <span className="text-sm font-normal text-foreground-400 ml-2">共 {comic.chapters.length} 话</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-accent-50 border border-background-200 hover:border-accent-200 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 text-xs font-medium flex-shrink-0 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                        {chapter.number}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-foreground-900 truncate">{chapter.title}</h3>
                        <p className="text-xs text-foreground-400">{chapter.updatedAt}</p>
                      </div>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-accent-500 transition-colors flex-shrink-0"></i>
                  </div>
                ))}
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="mt-16" data-product-shop>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                  相关
                  <span className="font-heading italic text-primary-500">推荐</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {relatedComics.map((rc) => (
                    <ComicCard key={rc.id} comic={rc} />
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}